import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import DOMPurify from "dompurify";
import RichTextEditor from "./RichTextEditor";

interface EmailTemplateEditorProps {
  label: string;
  description: string;
  subjectKey: string;
  bodyKey: string;
  placeholders: string[];
  subjectValue: string;
  bodyValue: string;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
}

type ViewMode = "richtext" | "html";

/**
 * Converts plain text with newlines to basic HTML (used for initial migration).
 */
function plainToHtml(text: string): string {
  if (text.startsWith("<")) return text; // already HTML
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/• /g, "&#8226; ")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>")
    .replace(/^/, "<p>")
    .replace(/$/, "</p>");
}

const EmailTemplateEditor = ({
  label,
  description,
  subjectKey,
  bodyKey,
  placeholders,
  subjectValue,
  bodyValue,
  onSubjectChange,
  onBodyChange,
}: EmailTemplateEditorProps) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("richtext");

  // Convert plain text to HTML on first render if needed
  const htmlBody = useMemo(() => plainToHtml(bodyValue), [bodyValue]);

  return (
    <div className="p-6 border border-border bg-card space-y-6">
      <div>
        <h3 className="text-subheadline mb-1">{label}</h3>
        <p className="text-caption">{description}</p>
      </div>

      <div>
        <label className="text-micro block mb-2">{t("emailEditor.subject")}</label>
        <input
          type="text"
          value={subjectValue}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary"
        />
      </div>

      {/* Mode Toggle */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-micro">{t("emailEditor.content")}</label>
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("richtext")}
              className={cn(
                "px-3 py-1 text-xs transition-colors",
                viewMode === "richtext"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:text-foreground"
              )}
            >
              RTF
            </button>
            <button
              type="button"
              onClick={() => setViewMode("html")}
              className={cn(
                "px-3 py-1 text-xs transition-colors border-l border-border",
                viewMode === "html"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:text-foreground"
              )}
            >
              HTML
            </button>
          </div>
        </div>

        {viewMode === "richtext" ? (
          <RichTextEditor content={htmlBody} onChange={onBodyChange} />
        ) : (
          <div className="space-y-3">
            <textarea
              value={bodyValue}
              onChange={(e) => onBodyChange(e.target.value)}
              rows={10}
              className="w-full p-4 border border-border bg-background text-body font-mono text-xs resize-none focus:outline-none focus:border-primary"
            />
            <div className="border border-border bg-background p-4">
              <span className="text-micro block mb-2">{t("emailEditor.preview")}</span>
              <div
                className="text-sm text-foreground leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlBody) }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-secondary/30 border border-border">
        <span className="text-micro block mb-2">{t("emailEditor.availablePlaceholders")}</span>
        <div className="flex flex-wrap gap-2">
          {placeholders.map((ph) => (
            <code
              key={ph}
              className="text-xs px-2 py-1 bg-muted text-muted-foreground border border-border"
            >
              {ph}
            </code>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
